import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Button, EmptyState, ErrorState, Skeleton } from "../../components/ui.tsx";
import { get } from "../../lib/api.ts";
import { planKey, usePlan } from "../../lib/plan.tsx";
import type { AuditEvent } from "../../lib/types.ts";
import { formatInstantBR } from "../../../shared/dates.ts";
import { useUrlState } from "../../lib/nav.tsx";

const LABELS: Record<string, string> = {
  "plan.created": "Oficina criada",
  "plan.updated": "Identificação da oficina alterada",
  "institution.renamed": "Nome da instituição alterado",
  "plan.access_deadline_changed": "Prazo de acesso alterado",
  "axis.created": "Eixo criado",
  "axis.updated": "Eixo alterado",
  "axis.archived": "Eixo arquivado",
  "unit.created": "Unidade criada",
  "unit.updated": "Unidade alterada",
  "unit.archived": "Unidade arquivada",
  "group.created": "Grupo criado",
  "group.updated": "Grupo renomeado",
  "group.members_replaced": "Membros do grupo alterados",
  "group.archived": "Grupo arquivado",
  "membership.updated": "Participante alterado",
  "invitation.created": "Convite enviado",
  "invitation.reissued": "Convite reenviado",
  "invitation.revoked": "Convite revogado",
  "invitation.accepted": "Convite aceito",
  "objective.created": "Objetivo criado",
  "objective.updated": "Objetivo alterado",
  "objective.archived": "Objetivo arquivado",
  "key_result.created": "KR criado",
  "key_result.updated": "KR alterado",
  "key_result.archived": "KR arquivado",
  "initiative.created": "Iniciativa criada",
  "initiative.saved": "Rascunho salvo",
  "initiative.submitted": "Iniciativa submetida",
  "initiative.validated": "Planejamento validado",
  "initiative.changes_requested": "Ajustes solicitados",
  "initiative.reopened": "Iniciativa reaberta",
  "initiative.archived": "Iniciativa arquivada",
  "context.changed": "Contexto alterado com reabertura",
  "agenda_event.created": "Ação da agenda criada",
  "agenda_event.saved": "Ação da agenda salva",
  "agenda_event.archived": "Ação da agenda arquivada",
};

function detail(e: AuditEvent): string | null {
  const m = e.metadata;
  if (typeof m.reason === "string" && m.reason) return `Justificativa: ${m.reason}`;
  if (typeof m.comment === "string" && m.comment) return `Comentário: ${m.comment}`;
  if (typeof m.name === "string") return m.name;
  if (typeof m.title === "string") return m.title;
  if (typeof m.email === "string") return m.email;
  return null;
}

export function HistoryTab() {
  const plan = usePlan();
  const [u, setU] = useUrlState({ pagina: "1" });
  const page = Number(u.pagina) || 1;
  const setPage = (fn: (p: number) => number) => setU({ pagina: String(fn(page)) });
  const q = useQuery({
    queryKey: planKey(plan.id, "audit", page),
    queryFn: () => get<{ data: AuditEvent[]; meta: { total: number; page_size: number } }>(`/plans/${plan.id}/audit?page=${page}&page_size=50`),
    placeholderData: keepPreviousData,
  });
  if (q.isPending) return <Skeleton lines={6} />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const pages = Math.max(1, Math.ceil(q.data.meta.total / q.data.meta.page_size));
  if (q.data.data.length === 0) return <EmptyState title="Ainda não há eventos registrados." />;
  return (
    <>
      <ol className="divide-y divide-line overflow-hidden rounded-panel border border-line bg-surface shadow-e1">
        {q.data.data.map((e) => (
          <li key={e.id} className="px-4 py-3">
            <p className="text-sm font-semibold">{LABELS[e.event_type] ?? e.event_type}</p>
            <p className="text-xs text-muted">{formatInstantBR(e.occurred_at)} · {e.actor_name ?? "Sistema"}</p>
            {detail(e) && <p className="mt-1 text-sm">{detail(e)}</p>}
          </li>
        ))}
      </ol>
      <div className="mt-3 flex items-center justify-end gap-2 text-sm">
        <Button variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</Button>
        <span>Página {page} de {pages}</span>
        <Button variant="secondary" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Próxima</Button>
      </div>
    </>
  );
}
